import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtEUR } from "../../lib/api";
import { StatusBadge } from "../StatusBadge";
import {
  CalendarCheck, UserX, UserCheck, Car, CheckCircle2, AlertTriangle, Users, MapPinOff, ChevronRight,
  TrendingUp, TrendingDown, Radar, Clock, Plane, ArrowRight, Truck,
} from "lucide-react";

const TONES = {
  gold: "text-[#E9C46A] bg-[#D4A72C]/10 border-[#D4A72C]/35", sky: "text-sky-300 bg-sky-500/10 border-sky-500/30",
  emerald: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30", rose: "text-rose-300 bg-rose-500/10 border-rose-500/30",
  violet: "text-violet-300 bg-violet-500/10 border-violet-500/30", slate: "text-slate-300 bg-white/5 border-white/10",
};

export function useOps() {
  const [o, setO] = useState(null);
  useEffect(() => {
    const load = () => api.get("/dashboard/ops").then(r => setO(r.data)).catch(() => setO(null));
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);
  return o;
}

export function ExecPanel({ title, sub, action, onAction, children, testid, className = "" }) {
  return (
    <section className={`exec-card flex flex-col min-w-0 ${className}`} data-testid={testid}>
      <div className="px-4 sm:px-5 py-3.5 border-b border-[#D4A72C]/12 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-display text-base md:text-lg font-bold text-white truncate">{title}</h2>
          {sub && <div className="text-[11px] text-slate-500 truncate">{sub}</div>}
        </div>
        {action && <button type="button" onClick={onAction} data-testid={`${testid}-action`} className="text-xs text-[#E9C46A] hover:text-[#FFE08A] font-semibold flex items-center gap-0.5 whitespace-nowrap min-h-[36px]">{action}<ChevronRight size={13} /></button>}
      </div>
      <div className="flex-1">{children}</div>
    </section>
  );
}

function Tile({ icon: I, label, value, hint, tone, onClick, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className="exec-tile text-left p-3 min-h-[84px] flex flex-col justify-between min-w-0">
      <div className="flex items-start justify-between gap-1.5">
        <div className="text-[11px] leading-tight text-slate-400">{label}</div>
        <div className={`w-7 h-7 rounded-lg border flex items-center justify-center flex-shrink-0 ${TONES[tone]}`}><I size={14} /></div>
      </div>
      <div>
        <div className="font-mono-num text-xl font-extrabold text-white leading-none mt-1">{value}</div>
        {hint && <div className="mt-1 text-[10px] text-slate-500 leading-tight">{hint}</div>}
      </div>
    </button>
  );
}

export function OpsStatus({ o, d }) {
  const nav = useNavigate();
  const b = o.bookings;
  const go = (status) => nav(`/prenotazioni?date=${o.date}${status ? `&status=${encodeURIComponent(status)}` : ""}`);
  const drvAvail = d?.drivers_available != null ? Math.max(0, d.drivers_available - o.drivers_busy) : null;
  return (
    <ExecPanel title="Stato operativo oggi" sub="Prenotazioni, autisti e flotta" testid="ops-overview" className="lg:col-span-3">
      <div className="p-3 sm:p-4 grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-2">
        <Tile icon={CalendarCheck} label="Prenotazioni oggi" value={b.total} hint={`${b.confirmed} confermate`} tone="gold" onClick={() => go(null)} testid="ops-tile-today" />
        <Tile icon={UserX} label="Da assegnare" value={b.unassigned} hint={b.unassigned ? "Richiede attenzione" : "Tutto assegnato"} tone={b.unassigned ? "rose" : "slate"} onClick={() => go("Confermata")} testid="ops-tile-unassigned" />
        <Tile icon={UserCheck} label="Assegnati" value={b.assigned} tone="violet" onClick={() => go("Assegnata")} testid="ops-tile-assigned" />
        <Tile icon={Car} label="In corso" value={b.in_progress} tone="sky" onClick={() => go("In corso")} testid="ops-tile-inprogress" />
        <Tile icon={CheckCircle2} label="Completati" value={b.completed} hint={b.total ? `${Math.round((b.completed / b.total) * 100)}% del totale` : null} tone="emerald" onClick={() => go("Completata")} testid="ops-tile-completed" />
        <Tile icon={AlertTriangle} label="Da verificare" value={b.to_check} hint="Ritardi/conflitti Sentinel" tone={b.to_check ? "rose" : "slate"} onClick={() => nav("/sentinel")} testid="ops-tile-tocheck" />
        <Tile icon={Users} label="Autisti disponibili" value={drvAvail ?? "—"} hint={`${d?.drivers_total ?? 0} in organico`} tone="emerald" onClick={() => nav("/autisti")} testid="ops-tile-drivers-free" />
        <Tile icon={Users} label="Autisti impegnati" value={o.drivers_busy} hint="Con servizio in corso" tone="sky" onClick={() => go("In corso")} testid="ops-tile-drivers-busy" />
        <Tile icon={Car} label="Veicoli disponibili" value={d?.vehicles_available ?? "—"} hint={`su ${d?.vehicles_total ?? 0}`} tone="gold" onClick={() => nav("/flotta")} testid="ops-tile-vehicles" />
        <Tile icon={Truck} label="Veicoli impegnati" value={d?.vehicles_busy ?? "—"} hint={`su ${d?.vehicles_total ?? 0}`} tone="sky" onClick={() => nav("/flotta")} testid="ops-tile-vehicles-busy" />
      </div>
    </ExecPanel>
  );
}

export function NextServices({ o }) {
  const nav = useNavigate();
  return (
    <ExecPanel title="Prossimi servizi" sub="Oggi · in ordine di orario" action="Vedi tutti" onAction={() => nav(`/prenotazioni?date=${o.date}`)} testid="ops-next-services">
      <div className="divide-y divide-white/5 max-h-[360px] overflow-y-auto">
        {o.next_services.map(s => (
          <button key={s.id} type="button" onClick={() => nav(`/prenotazioni?open=${s.id}`)} data-testid={`ops-next-${s.id}`}
            className="w-full text-left px-4 sm:px-5 py-3 hover:bg-[#D4A72C]/5 flex items-center gap-3 min-h-[56px]">
            <div className="font-mono-num text-[#E9C46A] font-bold text-sm w-11 flex-shrink-0">{s.time || "--:--"}</div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] text-white flex items-center gap-1 min-w-0"><span className="truncate">{s.pickup || "-"}</span><ArrowRight size={11} className="text-[#D4A72C]/70 flex-shrink-0" /><span className="truncate">{s.destination || "-"}</span></div>
              <div className="text-[11px] text-slate-400 truncate">{s.customer_name || "Cliente non indicato"}</div>
              <div className={`text-[11px] truncate ${s.driver_name ? "text-slate-500" : "text-rose-300"}`} data-testid={`ops-next-assign-${s.id}`}>{s.driver_name ? `${s.driver_name}${s.plate ? ` · ${s.plate}` : ""}` : "Da assegnare"}</div>
            </div>
            <StatusBadge status={s.status} />
          </button>
        ))}
        {o.next_services.length === 0 && <div className="text-slate-500 text-sm text-center py-10" data-testid="ops-next-empty">Nessun servizio in programma oggi</div>}
      </div>
    </ExecPanel>
  );
}

const ALERT_ICON = { missing_driver: UserX, conflict_driver: AlertTriangle, delay_risk: Clock, stale_in_progress: Clock, missing_vehicle: Car,
  missing_data: AlertTriangle, travel_id: Plane, cancelled_operative: AlertTriangle, gps_stale: MapPinOff, gps_silent: MapPinOff, pickup_risk: Clock };

export function QuickAlerts({ o }) {
  const nav = useNavigate();
  const last = o.sentinel_last_run ? new Date(o.sentinel_last_run).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) : null;
  return (
    <ExecPanel title="Segnalazioni rapide" sub={last ? `Sentinel · ultimo controllo ${last}` : "Sentinel"} action="Vedi tutte" onAction={() => nav("/sentinel")} testid="ops-alerts" className="lg:col-span-2">
      <div className="divide-y divide-white/5">
        {o.alerts.map(a => {
          const I = ALERT_ICON[a.key] || Radar;
          return (
            <button key={a.key} type="button" onClick={() => nav(a.link)} data-testid={`ops-alert-${a.key}`} className="w-full text-left px-4 sm:px-5 py-3 hover:bg-rose-500/5 flex items-center gap-3 min-h-[56px]">
              <div className="w-8 h-8 rounded-lg border border-[#D4A72C]/30 bg-[#D4A72C]/8 flex items-center justify-center flex-shrink-0"><I size={15} className="text-[#E9C46A]" /></div>
              <div className="flex-1 min-w-0"><div className="text-sm text-white truncate">{a.label}</div><div className="text-[11px] text-slate-500 truncate">{a.hint}</div></div>
              <span className="font-mono-num font-extrabold text-rose-300">{a.count}</span>
              <ChevronRight size={14} className="text-slate-600 flex-shrink-0" />
            </button>
          );
        })}
        {o.alerts.length === 0 && <div className="text-slate-500 text-sm text-center py-10" data-testid="ops-alerts-empty">Nessuna segnalazione aperta</div>}
      </div>
    </ExecPanel>
  );
}

export function EconomicSummary({ d, todayRev }) {
  const ms = d?.monthly_series || [];
  const ym = new Date().toISOString().slice(0, 7);
  const idx = ms.findIndex(x => x.month === ym);
  const prev = idx > 0 ? ms[idx - 1]?.revenue : null;
  const cur = d?.revenue_month ?? 0;
  const delta = prev ? Math.round(((cur - prev) / prev) * 100) : null;
  const Row = ({ label, value, cls, testid }) => (
    <div className="exec-tile p-3 min-w-0"><div className="text-[11px] text-slate-400">{label}</div><div className={`font-mono-num text-base sm:text-lg font-extrabold whitespace-nowrap ${cls}`} data-testid={testid}>{value}</div></div>
  );
  return (
    <div className="p-4 sm:p-5 space-y-3" data-testid="ops-economic">
      <div className="flex flex-wrap items-end gap-x-3 gap-y-1">
        <div><div className="text-[11px] text-slate-400">Fatturato del mese</div><div className="font-mono-num text-2xl sm:text-3xl font-extrabold text-gold-metal whitespace-nowrap" data-testid="ops-revenue-month">{fmtEUR(cur)}</div></div>
        {delta != null ? (
          <div className={`text-xs font-bold flex items-center gap-1 pb-1 ${delta >= 0 ? "text-emerald-400" : "text-rose-400"}`} data-testid="ops-revenue-delta">
            {delta >= 0 ? <TrendingUp size={13} /> : <TrendingDown size={13} />}{delta > 0 ? "+" : ""}{delta}% <span className="text-slate-500 font-normal">vs mese prec.</span>
          </div>
        ) : <div className="text-[11px] text-slate-500 pb-1">Confronto non disponibile</div>}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Row label="Fatturato oggi" value={fmtEUR(todayRev)} cls="text-white" testid="ops-revenue-today" />
        <Row label="Margine del mese" value={d?.margin_month != null ? fmtEUR(d.margin_month) : "Non disponibile"} cls="text-white" testid="ops-margin" />
        <Row label="Da ricevere" value={fmtEUR(d?.outstanding_from_customers ?? 0)} cls="text-emerald-300" testid="ops-to-receive" />
        <Row label="Da pagare" value={fmtEUR(d?.outstanding_to_suppliers ?? 0)} cls="text-rose-300" testid="ops-to-pay" />
      </div>
    </div>
  );
}
